import React, { useMemo } from 'react';
import { TextField, Typography, Box, Icon, Popper } from '@material-ui/core';
import Autocomplete, { createFilterOptions } from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import useProviderData from '../hooks/useProviderData';
import { getGpuModel, gpuFullName, isAcceleratorOptional } from './gpuModels';

// --- Field extraction helpers --------------------------------------------
// Instance data shape varies by provider/API, so probe the common field
// names and fall back to parsing the display name when only a formatted
// string is available.

function getBareName(instance) {
    if (instance.instance_id) return instance.instance_id;
    const name = instance.name || '';
    return name.split(' - ')[0].trim() || name;
}

function getCpu(instance) {
    const v = instance.vcpus || instance.vCPU || instance.vcpu || instance.cpu || instance.CPUs || instance.cores;
    if (v) return v;
    const m = (instance.name || '').match(/vCPU[:\s]*([\d.]+)/i);
    return m ? m[1] : null;
}

function getMem(instance) {
    const v = instance.memory || instance.Memory || instance.ram || instance.RAM;
    if (v) return v;
    const m = (instance.name || '').match(/(?:Mem|RAM)[^:]*:\s*([\d.]+)/i);
    return m ? m[1] : null;
}

// Memory normalized to GB. Providers disagree on units: GCP reports GB (3.75,
// 15, ...) while IBM reports MB (122880 for 120 GB). Detect MB and convert.
// Heuristic: real instances stay well under ~64 GB RAM per vCPU, so a ratio
// far above that means the raw value is in MB. (No vCPU -> fall back to "clean
// multiple of 1024 and >= 1024" which catches MB without touching GB values.)
function getMemGB(instance) {
    const raw = getMem(instance);
    if (raw == null) return null;
    const mem = parseFloat(raw);
    if (!isFinite(mem)) return null;
    const cpu = parseFloat(getCpu(instance));
    const looksMB = isFinite(cpu) && cpu > 0
        ? mem / cpu > 64
        : mem >= 1024 && mem % 1024 === 0;
    const gb = looksMB ? mem / 1024 : mem;
    return Math.round(gb * 100) / 100; // drop float noise; String() trims trailing zeros
}

// Describe a shape's accelerator, or null for a plain CPU shape. Detection is
// conservative — an explicit count/type field, or a name containing "gpu" — so
// a CPU shape is never mislabelled. When the concrete model is resolvable
// (T4, A100, L4, ...) the label carries it ("1× NVIDIA T4") instead of a bare
// "1× GPU". `provider` is required to resolve the model, since the accelerator
// is encoded differently in each provider's naming convention.
//
// Returns { model, label, full, fixed }:
//   fixed=true  -> shape ships with the GPU (a2/g2, p3, NC*, ...)
//   fixed=false -> attach-optional (GCP n1); model is null and set at deploy time
function getGpuInfo(instance, provider) {
    const rawCount = instance.gpus ?? instance.gpu_count ?? instance.gpuCount ?? instance.accelerator_count ?? instance.gpu;
    // GCP reports gpus as a string ("1"), other providers as a number (1) -
    // coerce so a string count isn't silently treated as "no GPU".
    const count = typeof rawCount === 'string' ? parseFloat(rawCount) : rawCount;
    const type = instance.gpu_type || instance.acceleratorType || instance.accelerator_type;
    const name = getBareName(instance).toLowerCase();
    const looksGpu = name.includes('gpu');
    const hasCount = typeof count === 'number' && isFinite(count) && count > 0;

    if (hasCount || count === true || type || looksGpu) {
        const model = getGpuModel(instance, provider);
        const n = hasCount ? count : null;
        let label;
        if (model) label = n ? `${n}× ${model}` : model;
        else label = n ? `${n}× GPU` : 'GPU';
        return { model, label, full: model ? gpuFullName(model) : 'GPU', fixed: true };
    }

    // Attach-optional: no fixed GPU, but the shape accepts one (GCP n1). The
    // available models are zone-dependent and chosen in the cluster form.
    if (isAcceleratorOptional(getBareName(instance), provider)) {
        return { model: null, label: 'GPU optional', full: 'Supports optional GPU accelerators', fixed: false };
    }
    return null;
}

const CATEGORY_ORDER = ['General purpose', 'Compute optimized', 'Memory optimized', 'GPU'];

function getCategory(instance, provider) {
    const gpu = getGpuInfo(instance, provider);
    if (gpu && gpu.fixed) return 'GPU';
    const n = getBareName(instance).toLowerCase();
    if (/highmem|highmemory|ultramem|megamem|mem-opt/.test(n)) return 'Memory optimized';
    if (/highcpu|compute-opt|comput/.test(n)) return 'Compute optimized';
    return 'General purpose';
}

// CPU architecture: 'ARM' or 'x86'. Prefer the explicit `architecture` field
// (rarely populated today), otherwise detect from well-known ARM naming
// conventions per provider, defaulting to x86. Patterns are kept conservative
// to avoid mislabelling x86 instances as ARM.
function getArch(instance) {
    const explicit = (instance.architecture || instance.arch || instance.cpu_architecture || '').toString().toLowerCase();
    if (/arm|aarch/.test(explicit)) return 'ARM';
    if (/x86|amd64|x64|intel/.test(explicit)) return 'x86';

    const id = getBareName(instance).toLowerCase();
    if (/aarch64|graviton|ampere|kunpeng|axion|\barm\b/.test(id)) return 'ARM';

    const awsFamily = id.split('.')[0]; // e.g. "t4g" from "t4g.medium"
    if (/^[a-z]+[0-9]+g[a-z]*$/.test(awsFamily) || awsFamily === 'a1') return 'ARM'; // AWS Graviton (m6g, c7gn, t4g, a1)
    if (/^(t2a|c4a)-/.test(id)) return 'ARM';            // GCP Tau T2A / Axion C4A
    if (/\.a1\.|\ba1\b/.test(id)) return 'ARM';          // Oracle/OCI Ampere A1
    if (/^standard_[a-z]+[0-9]+p/.test(id)) return 'ARM'; // Azure Ampere Altra (Dpsv5, Epsv5, ...)

    return 'x86';
}

// Used for the selected-value text shown inside the input.
function formatInstanceDisplay(instance) {
    const name = instance.name || instance.instance_id;
    if (name && (name.includes('vCPU') || name.includes('Mem') || name.includes('RAM'))) {
        return name;
    }
    const specs = [];
    const cpu = getCpu(instance);
    if (cpu) specs.push(`vCPU: ${cpu}`);
    const mem = getMemGB(instance);
    if (mem != null) specs.push(`Mem (GB): ${mem}`);
    return specs.length > 0 ? `${name} - ${specs.join(', ')}` : name;
}

// --- Chip styles ----------------------------------------------------------
const chipBase = {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    borderRadius: 6,
    padding: '3px 8px',
    whiteSpace: 'nowrap',
};
const specChipStyle = { ...chipBase, color: '#334155', backgroundColor: '#f1f5f9', border: '1px solid #e2e8f0' };
const gpuChipStyle = { ...chipBase, color: '#fff', backgroundColor: '#8B5CF6', border: '1px solid #7C3AED' };
// Attach-optional GPU (e.g. GCP n1): outlined so it reads as "supported" rather
// than "included", distinguishing it from shapes that ship a fixed GPU.
const gpuOptionalChipStyle = { ...chipBase, color: '#7C3AED', backgroundColor: '#f5f3ff', border: '1px dashed #a78bfa' };
const armChipStyle = { ...chipBase, color: '#fff', backgroundColor: '#0d9488', border: '1px solid #0f766e' };
const x86ChipStyle = { ...chipBase, color: '#475569', backgroundColor: '#eef2f7', border: '1px solid #e2e8f0' };
const chipIconStyle = { fontSize: 15, marginRight: 4, color: '#64748b' };

// By default the Autocomplete popper is pinned to the input width, which
// truncates long instance names (e.g. Oracle "VM.Standard.E4.Flex"). Let it
// grow to fit the widest row while never shrinking below the input width.
function WidePopper(props) {
    const anchorWidth = props.style && props.style.width;
    return (
        <Popper
            {...props}
            style={{ ...(props.style || {}), width: 'auto', minWidth: anchorWidth, maxWidth: '90vw' }}
            placement="bottom-start"
        />
    );
}

export default function InstanceSizeSelector({ provider, value, onChange, allowedInstanceIds, label = 'Instance Size', required = true, className, disabled = false }) {
    const classes = useStyles();
    const { instanceSizes } = useProviderData(provider);

    // Provider-aware search: match on name, specs, and the resolved GPU model
    // (short + full) so "A100" / "NVIDIA T4" find the right shapes.
    const filterOptions = useMemo(() => createFilterOptions({
        stringify: (option) => {
            const gpu = getGpuInfo(option, provider);
            const gpuText = gpu ? `${gpu.label} ${gpu.full || ''}` : '';
            return `${getBareName(option)} ${getCpu(option) || ''} ${getMemGB(option) || ''} ${gpuText} ${getArch(option)} ${getCategory(option, provider)}`;
        },
    }), [provider]);

    const visibleInstances = useMemo(() => {
        let list = instanceSizes;
        if (allowedInstanceIds && allowedInstanceIds.length > 0) {
            list = list.filter((s) => allowedInstanceIds.includes(s.instance_id));
        }
        // Sort by category so MUI Autocomplete groups options correctly
        // (it groups consecutive items, so same-category rows must be adjacent).
        return [...list].sort(
            (a, b) => CATEGORY_ORDER.indexOf(getCategory(a, provider)) - CATEGORY_ORDER.indexOf(getCategory(b, provider))
        );
    }, [instanceSizes, allowedInstanceIds, provider]);

    const selectedInstance = useMemo(
        () => visibleInstances.find((s) => s.instance_id === value) || null,
        [visibleInstances, value]
    );

    return (
        <Autocomplete
            className={clsx(classes.input, className || "flex-1")}
            options={visibleInstances}
            PopperComponent={WidePopper}
            filterOptions={filterOptions}
            groupBy={(option) => getCategory(option, provider)}
            getOptionLabel={(option) => formatInstanceDisplay(option)}
            getOptionSelected={(option, val) => option.instance_id === val?.instance_id}
            value={selectedInstance}
            onChange={(_, newValue) => onChange(newValue?.instance_id || '')}
            disabled={disabled}
            noOptionsText="No matching instance types"
            renderOption={(option) => {
                const cpu = getCpu(option);
                const mem = getMemGB(option);
                const gpu = getGpuInfo(option, provider);
                const arch = getArch(option);
                const isSelected = value === option.instance_id;
                return (
                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" style={{ gap: 24, padding: '2px 0' }}>
                        <Typography variant="body2" style={{ fontWeight: isSelected ? 700 : 500, color: '#1e293b', whiteSpace: 'nowrap' }}>
                            {getBareName(option)}
                        </Typography>
                        <Box display="flex" alignItems="center" style={{ gap: 6, flexShrink: 0 }}>
                            {cpu && (
                                <span style={specChipStyle}>
                                    <Icon style={chipIconStyle}>developer_board</Icon>
                                    {cpu} vCPU
                                </span>
                            )}
                            {mem && (
                                <span style={specChipStyle}>
                                    <Icon style={chipIconStyle}>memory</Icon>
                                    {mem} GB
                                </span>
                            )}
                            {gpu && (
                                <span
                                    style={gpu.fixed ? gpuChipStyle : gpuOptionalChipStyle}
                                    title={gpu.full}
                                >
                                    {gpu.label}
                                </span>
                            )}
                            <span style={arch === 'ARM' ? armChipStyle : x86ChipStyle}>{arch}</span>
                        </Box>
                    </Box>
                );
            }}
            renderGroup={(params) => (
                <li key={params.key}>
                    <div
                        style={{
                            position: 'sticky',
                            top: -8,
                            zIndex: 1,
                            backgroundColor: '#f8fafc',
                            color: '#475569',
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.06em',
                            padding: '6px 16px',
                            borderBottom: '1px solid #e2e8f0',
                        }}
                    >
                        {params.group}
                    </div>
                    <ul style={{ padding: 0 }}>{params.children}</ul>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    variant="outlined"
                    required={required}
                    fullWidth
                    placeholder="Search instance types..."
                />
            )}
            ListboxProps={{
                style: { maxHeight: 320 },
            }}
        />
    );
}
